import React from 'react';
import { Star, StarHalf } from 'lucide-react';

interface StarRatingProps {
  rating: number;
  max?: number;
  size?: number;
  editable?: boolean;
  onChange?: (rating: number) => void;
  className?: string;
}

export const StarRating: React.FC<StarRatingProps> = ({
  rating,
  max = 5,
  size = 16,
  editable = false,
  onChange,
  className = ""
}) => {
  const [hoverRating, setHoverRating] = React.useState(0);

  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = max - fullStars - (hasHalfStar ? 1 : 0);

  const handleClick = (value: number) => {
    if (editable && onChange) {
      onChange(value);
    }
  };

  const handleMouseEnter = (value: number) => {
    if (editable) {
      setHoverRating(value);
    }
  };

  const handleMouseLeave = () => {
    if (editable) {
      setHoverRating(0);
    }
  };

  return (
    <div 
      className={`flex items-center gap-0.5 ${className}`}
      onMouseLeave={handleMouseLeave}
    >
      {[...Array(max)].map((_, i) => {
        const starValue = i + 1;
        const isActive = editable && hoverRating > 0 ? starValue <= hoverRating : starValue <= rating;
        const isHalf = !editable && starValue === fullStars + 1 && hasHalfStar;

        return (
          <button
            key={i}
            type="button"
            disabled={!editable}
            onClick={() => handleClick(starValue)}
            onMouseEnter={() => handleMouseEnter(starValue)}
            className={`transition-transform ${editable ? 'hover:scale-125 cursor-pointer' : 'cursor-default'}`}
          >
            {isHalf ? (
              <div className="relative">
                <Star size={size} className="text-gray-300" />
                <div className="absolute inset-0 overflow-hidden w-1/2">
                  <Star size={size} className="text-brand-orange fill-brand-orange" />
                </div>
              </div>
            ) : (
              <Star
                size={size}
                className={isActive ? 'text-brand-orange fill-brand-orange' : 'text-gray-300'}
              />
            )}
          </button>
        );
      })}
    </div>
  );
};
